import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, Download, ShieldCheck, User, FileCheck, Clock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ExportDataPage = () => {
  const { user } = useOutletContext();
  const [exporting, setExporting] = useState(false);
  const [lastExport, setLastExport] = useState(null);

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/export/pdf`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `dados_pessoais_${user.user_id}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setLastExport(new Date());
      toast.success('PDF exportado com sucesso!');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Erro ao exportar PDF');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="export-data-page">
      <div>
        <h1 className="text-4xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Exportar Dados Pessoais
        </h1>
        <p className="text-slate-600">
          Direito à portabilidade dos dados - Artigo 20º do RGPD
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#10B981]" />
              Ficha de Dados Pessoais
            </CardTitle>
            <CardDescription>
              Exporte todos os seus dados pessoais em formato PDF
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-[#0F172A] mb-2">O que está incluído:</h4>
              <ul className="text-sm text-slate-600 space-y-2">
                <li className="flex items-center gap-2">
                  <User className="w-4 h-4 text-blue-500" />
                  Dados de identificação (nome, email, perfil)
                </li>
                <li className="flex items-center gap-2">
                  <FileCheck className="w-4 h-4 text-blue-500" />
                  Todos os consentimentos ativos e histórico
                </li>
                <li className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-blue-500" />
                  Informação sobre responsável pelo tratamento
                </li>
                <li className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-blue-500" />
                  Direitos do titular e contactos
                </li>
              </ul>
            </div>

            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                <h4 className="font-semibold text-[#0F172A] mb-2">Os Seus Dados</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Nome:</span>
                    <span className="font-medium text-[#0F172A]">{user?.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Email:</span>
                    <span className="font-medium text-[#0F172A]">{user?.email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Perfil:</span>
                    <Badge variant="outline" className="capitalize">{user?.role}</Badge>
                  </div>
                </div>
              </div>

              <Button
                data-testid="export-pdf-button"
                onClick={handleExportPDF}
                disabled={exporting}
                className="w-full bg-[#10B981] hover:bg-[#10B981]/90 h-12"
              >
                {exporting ? (
                  <>
                    <Clock className="w-4 h-4 mr-2 animate-spin" />
                    A gerar PDF...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Exportar PDF
                  </>
                )}
              </Button>

              {lastExport && (
                <p className="text-xs text-slate-500 text-center">
                  Última exportação: {lastExport.toLocaleString('pt-PT')}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-[#3B82F6]" />
              Direitos RGPD
            </CardTitle>
            <CardDescription>
              Os seus direitos conforme o Regulamento Geral sobre a Proteção de Dados
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              {[
                { article: 'Art. 15º', title: 'Direito de Acesso', desc: 'Saber que dados são tratados e como' },
                { article: 'Art. 16º', title: 'Direito de Retificação', desc: 'Corrigir dados incorretos ou incompletos' },
                { article: 'Art. 17º', title: 'Direito ao Apagamento', desc: 'Solicitar eliminação dos dados' },
                { article: 'Art. 18º', title: 'Direito à Limitação', desc: 'Limitar o tratamento dos dados' },
                { article: 'Art. 20º', title: 'Direito à Portabilidade', desc: 'Receber dados em formato estruturado' },
                { article: 'Art. 21º', title: 'Direito de Oposição', desc: 'Opor-se ao tratamento dos dados' }
              ].map((right) => (
                <div key={right.article} className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="outline" className="text-xs">{right.article}</Badge>
                    <h4 className="font-semibold text-[#0F172A] text-sm">{right.title}</h4>
                  </div>
                  <p className="text-xs text-slate-600">{right.desc}</p>
                </div>
              ))}
            </div>

            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <h4 className="font-semibold text-amber-800 mb-2 text-sm">Precisa de Ajuda?</h4>
              <p className="text-xs text-amber-700">
                Para exercer os seus direitos ou esclarecer dúvidas, contacte o nosso 
                Encarregado de Proteção de Dados (DPO) através de: <strong>info@privaxia.eu</strong>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Additional Info */}
      <Card className="border-slate-200 bg-slate-50">
        <CardHeader>
          <CardTitle className="text-lg">Informação sobre Exportação de Dados</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
            <div>
              <h4 className="font-semibold text-[#0F172A] mb-2">Formato PDF</h4>
              <p className="text-slate-600">
                O documento é gerado em formato PDF, compatível com qualquer dispositivo e fácil de arquivar.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-[#0F172A] mb-2">Dados Completos</h4>
              <p className="text-slate-600">
                Inclui todos os dados pessoais tratados pela organização, consentimentos e histórico.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-[#0F172A] mb-2">Conformidade Legal</h4>
              <p className="text-slate-600">
                O documento cumpre os requisitos dos Artigos 13º e 14º do RGPD sobre informação ao titular.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ExportDataPage;
